// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { EMPTY_VALUE } from "@adl/XDL/constants";
import { color, xScreen } from "@adl/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import { InvoiceModel, InvoiceSettingsModel } from "@insite/client-framework/Types/ApiModels";
import InvoiceDetailPageTypeLink from "@insite/content-library/Components/InvoiceDetailPageTypeLink";
import { StyledButton } from "@insite/mobius/Clickable";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    invoice: InvoiceModel;
    session: Session;
    invoiceSettings: InvoiceSettingsModel;
    extendedStyles?: InvoiceSummaryCardStyles;
}

type Props = OwnProps;

export interface InvoiceSummaryCardStyles {
    dataCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
}

export const invoiceSummaryCardStyles: InvoiceSummaryCardStyles = {
    shipToCell: {
        css: css`
            max-width: 375px;
            ${xScreen("lte", "md")} {
                max-width: initial;
            }
        `,
        typographyProps: {
            css: css`
                overflow: hidden;
                text-overflow: ellipsis;
                display: block;
            `,
        },
    },
    dataCell: {
        css: css`
            & > ${StyledButton}, & > ${StyledButton} > ${TypographyStyle} {
                font-size: 15px;
                letter-spacing: 0;
                line-height: 26px;
                font-weight: normal;
                color: ${color("text.main")};
                &:hover {
                    color: ${color("common.ui1")};
                }
            }
        `,
    },
};

const InvoiceSummaryCard: FC<Props> = ({ invoice, session, invoiceSettings, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(invoiceSummaryCardStyles, extendedStyles));

    const data = {
        id: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        date: invoice.invoiceDate
            ? getLocalizedDateTime({
                  dateTime: new Date(invoice.invoiceDate),
                  language: session.language,
                  options: {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                  },
              })
            : "",
        dueDate: invoice.dueDate
            ? getLocalizedDateTime({
                  dateTime: new Date(invoice.dueDate),
                  language: session.language,
                  options: {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                  },
              })
            : "",
        shipTo: invoice.btAddress1
            ? `${invoice.btAddress1}.${invoice.shipToCity || ""} ${invoice.shipToState || ""} ${
                  invoice.shipToPostalCode || ""
              }`
            : "",
        total: invoice.invoiceTotalDisplay,
        poNumber: invoice.customerPO,
        jobName: invoice.properties.jobName,
        balance: invoice.currentBalanceDisplay,
    };

    // XNG-Changes: change structure to Table
    return (
        <>
            <DataTableCell data-th={translate("Date")} {...styles.dataCell}>
                {data.date}
            </DataTableCell>
            <DataTableCell data-th={translate("Invoice #")} {...styles.dataCell}>
                <InvoiceDetailPageTypeLink
                    title={data.invoiceNumber || EMPTY_VALUE}
                    invoiceNumber={invoice.invoiceNumber}
                />
            </DataTableCell>
            <DataTableCell data-th={translate("PO #")} {...styles.dataCell}>
                {data.poNumber || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("Job-Name")} {...styles.dataCell}>
                {data.jobName || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("Ship To / Pick Up")} {...styles.shipToCell}>
                {data.shipTo || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("Due Date")} {...styles.dataCell}>
                {data.dueDate || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("Total Amount")} {...styles.dataCell}>
                {data.total || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("Current Balance")} {...styles.dataCell}>
                {data.balance || EMPTY_VALUE}
            </DataTableCell>
        </>
    );
};

export default InvoiceSummaryCard;
